import React, { useState, useEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Alert from "@mui/material/Alert";
import LinearProgress from "@mui/material/LinearProgress";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import { useDispatch, useSelector } from "react-redux";
import { api, getSupabase } from "common";

const HEADERS = ["nome", "sobrenome", "email", "telefone"];
const HEADERS_LABEL = { nome: "Nome", sobrenome: "Sobrenome", email: "E-mail", telefone: "Telefone" };

function parseCSV(text) {
  const lines = text.replace(/^\uFEFF/, "").trim().split(/\r?\n/).filter(Boolean);
  if (!lines.length) return { headers: [], rows: [] };
  const delim = lines[0].includes(";") ? ";" : lines[0].includes("\t") ? "\t" : ",";
  const headers = lines[0].split(delim).map(h => h.trim().toLowerCase().replace(/"/g, ""));
  const rows = lines.slice(1).map((l, idx) => {
    const cols = l.split(delim).map(c => c.trim().replace(/^"|"$/g, ""));
    const obj = { _idx: idx + 2 };
    headers.forEach((h, i) => (obj[h] = cols[i] || ""));
    return obj;
  });
  return { headers, rows };
}

function validateRow(r) {
  const errs = [];
  if (!r.nome) errs.push("nome obrigatório");
  if (!r.sobrenome) errs.push("sobrenome obrigatório");
  if (!r.email || !r.email.includes("@")) errs.push("e-mail inválido");
  if (!r.telefone || r.telefone.replace(/\D/g, "").length < 8) errs.push("telefone inválido");
  return errs;
}

function downloadBlob(content, mime, filename) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function buildTemplateCSV() {
  const rows = [
    HEADERS.map(h => HEADERS_LABEL[h]).join(","),
    "João,Silva,joao.silva@exemplo.com,31999990001",
    "Maria,Santos,maria.santos@exemplo.com,31999990002",
  ];
  return rows.join("\n");
}

function buildTemplateXLS() {
  // ponytail: template .xls via HTML table (Excel abre nativamente); upgrade para `xlsx` quando precisar de .xlsx binário real
  const html = `<html><head><meta charset="UTF-8"></head><body><table border="1"><tr>${HEADERS.map(h=>`<th>${HEADERS_LABEL[h]}</th>`).join("")}</tr><tr><td>João</td><td>Silva</td><td>joao.silva@exemplo.com</td><td>31999990001</td></tr><tr><td>Maria</td><td>Santos</td><td>maria.santos@exemplo.com</td><td>31999990002</td></tr></table></body></html>`;
  return html;
}

export default function UserBulkImportDialog({ open, onClose, defaultRole }) {
  const dispatch = useDispatch();
  const auth = useSelector(s => s.auth);
  const { createUserWithAuth, fetchUsersOnce } = api;
  const isSuperAdmin = Boolean(auth?.profile?.is_superadmin || auth?.profile?.usertype === "superadmin" || auth?.profile?.role === "superadmin");
  const currentTenantId = auth?.profile?.tenant_id || auth?.profile?.tenantId || "";

  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState(currentTenantId);
  const [usertype, setUsertype] = useState(defaultRole || "driver");
  const [fileRows, setFileRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [parseError, setParseError] = useState("");
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (open) {
      setUsertype(defaultRole || "driver");
      setTenantId(currentTenantId || "");
      setFileRows([]); setFileName(""); setParseError(""); setResults(null); setProgress(0);
      const supabase = getSupabase?.();
      if (supabase) {
        supabase.from("tenants").select("id,name").order("name").then(({ data }) => data && setTenants(data));
      }
    }
  }, [open, defaultRole, currentTenantId]);

  const validRows = useMemo(() => fileRows.map(r => ({ ...r, _errs: validateRow(r) })), [fileRows]);
  const canImport = validRows.length > 0 && tenantId && usertype && !importing;

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name); setParseError(""); setResults(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        let text = ev.target.result;
        // ponytail: .xlsx binário não parseado sem lib `xlsx`; aceite CSV/TSV e .xls HTML-exportado; add `xlsx` quando precisar de binário
        if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
          // tenta tratar como texto (CSV/HTML table); binário real cairá em erro orientativo
          if (text.charCodeAt(0) === 0x50 && text.charCodeAt(1) === 0x4B) {
            setParseError("Arquivo .xlsx binário detectado. Salve como CSV (separado por vírgulas) no Excel e reenvie, ou instale a lib `xlsx`.");
            setFileRows([]); return;
          }
          // se for HTML table exportado, extrai <td>
          if (text.trim().startsWith("<html")) {
            const tdRe = /<td>(.*?)<\/td>/gi;
            const thRe = /<th>(.*?)<\/th>/gi;
            const headers = [...text.matchAll(thRe)].map(m => m[1].trim().toLowerCase());
            const cells = [...text.matchAll(tdRe)].map(m => m[1].trim());
            const rows = [];
            for (let i = 0; i < cells.length; i += headers.length) {
              const obj = { _idx: Math.floor(i / headers.length) + 2 };
              headers.forEach((h, j) => obj[h] = cells[i + j] || "");
              rows.push(obj);
            }
            setFileRows(rows); return;
          }
        }
        const { headers, rows } = parseCSV(text);
        const missing = HEADERS.filter(h => !headers.includes(h));
        if (missing.length) {
          setParseError(`Cabeçalhos obrigatórios faltando: ${missing.join(", ")}. Esperado: ${HEADERS.join(", ")}`);
          setFileRows([]);
          return;
        }
        setFileRows(rows);
      } catch (err) {
        setParseError(err.message);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleDownloadCSV = () => downloadBlob(buildTemplateCSV(), "text/csv;charset=utf-8", "template_usuarios.csv");
  const handleDownloadXLS = () => downloadBlob(buildTemplateXLS(), "application/vnd.ms-excel", "template_usuarios.xls");

  const handleImport = async () => {
    if (!tenantId) { setParseError("Selecione a Empresa."); return; }
    const invalid = validRows.filter(r => r._errs.length);
    if (invalid.length && !window.confirm(`${invalid.length} linha(s) com erro serão ignoradas. Continuar?`)) return;
    setImporting(true); setResults(null);
    const toImport = validRows.filter(r => r._errs.length === 0);
    const out = [];
    for (let i = 0; i < toImport.length; i++) {
      const r = toImport[i];
      setProgress(Math.round(((i) / toImport.length) * 100));
      const payload = {
        firstName: r.nome.trim(),
        lastName: r.sobrenome.trim(),
        email: r.email.trim().toLowerCase(),
        mobile: r.telefone.trim(),
        usertype,
        tenant_id: tenantId,
      };
      const res = await dispatch(createUserWithAuth(payload));
      out.push({ row: r._idx, email: payload.email, success: !!res?.success, error: res?.error || "" });
    }
    setProgress(100);
    setImporting(false);
    setResults(out);
    dispatch(fetchUsersOnce());
  };

  const successCount = results?.filter(r => r.success).length || 0;
  const failCount = results ? results.length - successCount : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}>
      <DialogTitle sx={{ bgcolor: "#0f172a", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", py: 2 }}>
        <Box>
          <Typography fontWeight={700}>Importar Usuários em Lote</Typography>
          <Typography variant="caption" sx={{ color: "#94a3b8" }}>Campos obrigatórios: nome, sobrenome, e-mail, telefone</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "#94a3b8" }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
        <Alert severity="info" sx={{ borderRadius: "8px" }}>
          Selecione a <b>Empresa</b> e o <b>Tipo de usuário</b>, baixe o template, preencha e importe. O arquivo deve conter as colunas: <b>{HEADERS.join(", ")}</b>.
        </Alert>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadCSV} sx={{ textTransform: "none" }}>Baixar template CSV</Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadXLS} sx={{ textTransform: "none" }}>Baixar template XLS</Button>
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormControl sx={{ minWidth: 220, flex: 1 }} size="small">
            <InputLabel>Empresa *</InputLabel>
            <Select value={tenantId} label="Empresa *" onChange={e => setTenantId(e.target.value)}>
              {!tenantId && <MenuItem value=""><em>Selecione</em></MenuItem>}
              {tenants.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              {!tenants.length && currentTenantId && <MenuItem value={currentTenantId}>{auth?.profile?.tenantName || currentTenantId}</MenuItem>}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel>Tipo de usuário *</InputLabel>
            <Select value={usertype} label="Tipo de usuário *" onChange={e => setUsertype(e.target.value)}>
              {isSuperAdmin ? [
                <MenuItem key="fleetadmin" value="fleetadmin">Gestor de Frota</MenuItem>,
                <MenuItem key="admin" value="admin">Administrador</MenuItem>,
                <MenuItem key="driver" value="driver">Condutor / Motorista</MenuItem>,
                <MenuItem key="customer" value="customer">Solicitante / Contato</MenuItem>,
              ] : [
                <MenuItem key="driver" value="driver">Condutor / Motorista</MenuItem>,
                <MenuItem key="customer" value="customer">Solicitante / Contato</MenuItem>,
              ]}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ border: "1px dashed #cbd5e1", borderRadius: "10px", p: 2, textAlign: "center", bgcolor: fileRows.length ? "#f0fdf4" : "#f8fafc" }}>
          <Typography variant="body2" fontWeight={600} color="#334155">{fileName || "Nenhum arquivo selecionado"}</Typography>
          <Typography variant="caption" color="#64748b">Aceita .csv, .xls (separado por vírgula, ponto-e-vírgula ou tab)</Typography>
          <Box sx={{ mt: 1 }}>
            <input id="bulk-file" type="file" accept=".csv,.xls,.xlsx,.txt" style={{ display: "none" }} onChange={handleFile} />
            <label htmlFor="bulk-file"><Button component="span" variant="contained" startIcon={<UploadFileIcon />} sx={{ bgcolor: "#0f172a", textTransform: "none" }}>Selecionar arquivo</Button></label>
            {fileRows.length > 0 && <Chip label={`${fileRows.length} linha(s)`} size="small" sx={{ ml: 1 }} color={validRows.some(r=>r._errs.length)?"warning":"success"} />}
          </Box>
        </Box>

        {parseError && <Alert severity="error">{parseError}</Alert>}
        {importing && <LinearProgress variant="determinate" value={progress} />}

        {validRows.length > 0 && !results && (
          <Box sx={{ maxHeight: 260, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
            <Table size="small" stickyHeader>
              <TableHead><TableRow>{HEADERS.map(h=><TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem" }}>{HEADERS_LABEL[h]}</TableCell>)}<TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>Status</TableCell></TableRow></TableHead>
              <TableBody>
                {validRows.slice(0, 100).map((r,i)=><TableRow key={i} sx={{ bgcolor: r._errs.length ? "#fef2f2" : "inherit" }}>
                  {HEADERS.map(h=><TableCell key={h} sx={{ fontSize: "0.8rem" }}>{r[h]}</TableCell>)}
                  <TableCell sx={{ fontSize: "0.75rem", color: r._errs.length ? "#dc2626" : "#16a34a" }}>{r._errs.length ? r._errs.join(", ") : "ok"}</TableCell>
                </TableRow>)}
              </TableBody>
            </Table>
            {validRows.length > 100 && <Typography variant="caption" sx={{ p: 1, display: "block" }}>Mostrando 100 de {validRows.length}</Typography>}
          </Box>
        )}

        {results && (
          <Alert severity={failCount===0?"success":successCount===0?"error":"warning"} sx={{ borderRadius: "8px" }}>
            Importação concluída: <b>{successCount} sucesso</b> / <b>{failCount} falha(s)</b> de {results.length} válido(s).
          </Alert>
        )}
        {results && (
          <Box sx={{ maxHeight: 200, overflow: "auto", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
            <Table size="small"><TableHead><TableRow><TableCell>Linha</TableCell><TableCell>E-mail</TableCell><TableCell>Resultado</TableCell></TableRow></TableHead>
              <TableBody>{results.map((r,i)=><TableRow key={i}><TableCell>{r.row}</TableCell><TableCell>{r.email}</TableCell><TableCell sx={{ color: r.success?"#16a34a":"#dc2626" }}>{r.success?"Criado":r.error}</TableCell></TableRow>)}</TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, bgcolor: "#f8fafc", borderTop: "1px solid #e2e8f0" }}>
        <Button onClick={onClose} sx={{ color: "#64748b" }}>{results ? "Fechar" : "Cancelar"}</Button>
        {!results && <Button variant="contained" disabled={!canImport} onClick={handleImport} sx={{ bgcolor: "#0f172a", textTransform: "none" }}>{importing ? `Importando ${progress}%...` : `Importar ${validRows.filter(r=>!r._errs.length).length} usuário(s)`}</Button>}
      </DialogActions>
    </Dialog>
  );
}
